import React, { useState, useEffect, useMemo, useRef } from 'react';
import GoogleMapReact from 'google-map-react';
import { Pointer, TailoredRegionPointer } from 'src/App/Dashboards/Market/Components';
import {
  formatGoogleMapsBounds,
  getCoordinatesFromWKT,
  getBoundedClusters,
  isPointWithinBounds,
  mapOptions,
  calculatePercentiles,
  convertCurrency,
  applySmartBoundsForCoords,
  parseFilterValToApiResponse,
  isEmpty,
  abbreviateNumber,
  calculateBounds,
  parseWKTStringToCoordinates,
  getGradeColor,
  getPoiPremiumColor,
} from 'src/utils/functions';
import {
  Bounds,
  Coordinates,
  Listing,
  MarketInMapViewport,
  Submarket,
} from 'src/interfaces/idashboard';
import { COLOR_PRIMARY_DARK, COLOR_RED, exchangeRates, POINTER_GREEN } from 'src/utils/const';
import {
  clusterListings,
  comparePolygonLimitsToBoundary,
  extractLatLong,
  generateChoroplethLegends,
  getNaturalBreaks,
  getPolygonPaths,
  getSubmarketColor,
} from 'src/App/Dashboards/Market/Pages/OverviewPage/helper';
import { MapListingCountInfoBox } from 'src/App/Dashboards/Market/Components/Alerts/MapListingCountInfoBoxAlert';
import MapFilter from './MapFilter';
import { GOOGLE_MAPS_API_KEY } from 'src/utils/keys';
import { useDispatch, useSelector } from 'react-redux';
import * as turf from '@turf/turf';
import { logAmplitudeEvent } from 'src/utils/analytics';
import {
  fetchVisibleMapSupply,
  getMapOverviewKpi,
  getMarketsInMapViewport,
} from 'src/store/Dashboard/actions';
import SubmarketPointer from 'src/App/Dashboards/Market/Components/Pointer/SubmarketPointer';
import DrawIcon from '@mui/icons-material/Draw';
import CancelIcon from '@mui/icons-material/Cancel';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import { Box, IconButton, Button, Grid, ClickAwayListener, Typography, Modal } from '@mui/material';
import DecisionDialog from 'src/App/Dashboards/Market/Components/DecisionDialog';
import {
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_100,
  COLOR_PRIMARY_2_300,
  COLOR_PRIMARY_2_500,
  COLOR_PRIMARY_2_700,
  NEIGHBOURHOOD_THEME,
} from 'src/utils/colors';
import HeatmapLegendBox from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/HeatmapLegendBox';
import { DropdownSelect2 } from 'src/App/Dashboards/Market/Components/Inputs';
import ClusterPointer from 'src/App/Dashboards/Market/Components/Pointer/ClusterPointer';
import CollapsibleDrawer from './CollapsibleDrawer';
import CloseIcon from '@mui/icons-material/Close';
import { ZoomButtons } from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/ZoomButtons';
import AppliedFiltersDisplay from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/AppliedFilterDisplay';
import SaveBoxPointer from 'src/App/Dashboards/Market/Components/Pointer/SaveBoxPointer';
import SubmarketMapMarket from './SubMarketsTab/SubmarketMapMarket';
import { calculateBoundsAll, calculateZoomLevel, formatPremium, getVisiblePoiLabels, sortPois } from './SubMarketsTab/helper';
import { HotspotLegend } from './SubMarketsTab/HotspotAnalysis';
import _ from 'lodash';
import drawSubmarketGif from 'src/App/assets/submarket_creation_vid.gif';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import MarketPointer from 'src/App/Dashboards/Market/Components/Pointer/MarketPointer';

const PoiMapPin = ({
  pin,
  name,
  premium,
  isHighlighted,
  onMouseEnter,
  onMouseLeave,
}: {
  pin: number;
  name: string;
  premium: string | null;
  lat: number;
  lng: number;
  isHighlighted: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  const label = premium ? `${premium} · ${name}` : name;
  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: isHighlighted ? COLOR_PRIMARY_2 : 'white',
        color: isHighlighted ? 'white' : COLOR_PRIMARY_2,
        fontWeight: 600,
        fontSize: '12px',
        borderRadius: '100px',
        padding: '4px 10px',
        boxShadow: '0 2px 4px #0002',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        zIndex: isHighlighted ? 10000 : 9999,
        whiteSpace: 'nowrap',
        maxWidth: isHighlighted ? '240px' : '36px',
        overflow: 'hidden',
        '& .poi-pin-num': { flexShrink: 0 },
        '&:hover .poi-pin-num': { width: 0, overflow: 'hidden', display: 'none' },
        ...(isHighlighted && { '& .poi-pin-num': { display: 'none' } }),
        '&:hover': {
          backgroundColor: COLOR_PRIMARY_2,
          color: 'white',
          maxWidth: '240px',
        },
      }}
    >
      <span className="poi-pin-num">#{pin}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </Box>
  );
};

const MAP_FILL_COLOR = POINTER_GREEN;
const CLUSTER_BREAK_ZOOM_LEVEL = 17;
const MIN_DRAWER_HEIGHT = 30; // 20% of viewport height
const MAX_DRAWER_HEIGHT = 60; // 60% of viewport height

const getLegendUnit = (neighbourhoodAnalysisKey, currency) => {
  if (
    neighbourhoodAnalysisKey == 'revenue' ||
    neighbourhoodAnalysisKey == 'revPar' ||
    neighbourhoodAnalysisKey == 'adr'
  ) {
    return [exchangeRates[currency].symbol, 'left'];
  } else if (
    neighbourhoodAnalysisKey == 'occupancy' ||
    neighbourhoodAnalysisKey == 'occupancy_rate' ||
    neighbourhoodAnalysisKey == 'yoy_revenue_change'
  ) {
    return ['%', 'right'];
  } else return ['', 'right'];
};

const Map = ({
  mapCenter,
  setMapCenter,
  marketID,
  matches,
  search,
  dashboardLayoutView,
  setDashboardLayoutView,
  isDisabled,
  isUserAllowed,
  revPotentialRangeInvestment,
  currency,
  handleOnSampleClick,
  filterActive,
  setShowPremiumDataDynamic,
  toggleDrawer,
  setFilterVal,
  filterVal,
  setFilterActive,
  noDataInMarket,
  topPerformingListingOnFocus,
  setListingsGridStartIndexOffset,
  topPerformingMarketOnFocus,
  setTopPerformingMarketOnFocus,
  marketsToCompareList,
  isTabChanging,
  setIsDynamicDataBtnVisible,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  zoom,
  setZoom,
  bounds,
  setBounds,
  visibleMapListings,
  visibleBookingListings,
  mapViewportListings,
  setSubmarketPolygonPaths,
  mapLoading,
  setMapLoading,
  OnBoardingSearchBox,
  showViewingLisingFromPaginationAlert,
  setShowViewingLisingFromPaginationAlert,
  setMapHeightOnMobileView,
  cachedMarketBedroomADR,
  cachedMarketData,
  callDynamicMarkers,
  callBookingMarkers,
  submarkets,
  hoveredSubmarketId,
  setHoveredSubmarketId,
  handleHideSubmarket,
  parentMarketDetails,
  savedClusters,
  setSavedClusters,
  getSubSectionHeading,
  getMapViewHeadings,
  OverViewMapSectionMarketStatsTooltip,
  isDynamicDataBtnVisible,
  SaveSubMarketButton,
  MainKpiMapView,
  isMapCollapsed,
  ListingCountHeader,
  ListingCardGrid,
  marketDisplayName,
  activeListingCountDataToUse,
  currentHoveredCardFromDataGrid,
  setCurrentHoveredCardFromDataGrid,
  FilterBarComponent,
  showFilterComingSoonAlert,
  setShowFilterComingSoonAlert,
  filterCacheLoading,
  filterApplied,
  listingSortingKey,
  sx,
  isSubmarketAccessAllowed,
  mapMode,
  setMapMode,
  heatmapMode,
  setHeatmapMode,
  pois,
  hoveredPoiIndex,
  setHoveredPoiIndex,
  hiddenSubmarkets,
  mapCopy,
  setMapCopy,
  mapsCopy,
  setMapsCopy,
  isNewSubmarketDrawingMode,
  setIsNewSubmarketDrawingMode,
  setTailoredSubmarketPolygon,
  setShowSubmarketCreationModal,
  isLoggedIn,
  submarketSortKey,
  setShowFullSubmarketMap,
  showFullSubmarketMap,
  mobileHeaderHeight,
  showSubmarketMobileLegend,
  setShowSubmarketMobileLegend,
  isFullOverviewMap,
}) => {
  const user = useSelector(state => (state as any).dashboard.user);
  const useFullMarkers = useSelector(state => (state as any).dashboard.useFullMarkers);
  const backendComputedClusters = useSelector(state => (state as any).dashboard.clusters);
  const marketsInMapViewport: { data: MarketInMapViewport[]; loading: boolean } = useSelector(
    state => (state as any).dashboard.marketsInMapViewport
  );
  const dispatch = useDispatch();

  const [tooltipPosition, setTooltipPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [hoveredSubmarketData, setHoveredSubmarketData] = useState<any>(null);
  const [hoveredClusterData, setHoveredClusterData] = useState<any>(null);
  const [hoveredClusterId, setHoveredClusterId] = useState<any>(null);
  const [hoveredClusterPolygon, setHoveredClusterPolygon] = useState<any>(null);
  const [clickedClusterId, setClickedClusterId] = useState<any>(null);
  const [rightClickedCluster, setRightClickedCluster] = useState<any>(null);

  const [searchPolygonCopy, setSearchPolygonCopy] = useState<any>(null);
  const [polygonPath, setPolygonPath] = useState<any>(null);
  const [parentPolygonCopy, setParentPolygonCopy] = useState<any>(null);

  const [neighbourhoodAnalysisKey, setNeighbourhoodAnalysisKey] = useState<string>('listing');
  const [neighbourhoodChoroplethLegends, setNeighbourhoodChoroplethLegends] = useState<any>({});
  const [clusterChoroplethLegends, setClusterChoroplethLegends] = useState<any>({});

  const [submarketDrawnOutOfParent, setSubmarketDrawnOutOfParent] = useState<boolean>(false);

  const isMarketTab: boolean =
    dashboardLayoutView !== 'compare' && dashboardLayoutView !== 'submarkets';

  const [polygonBounds, setPolygonBounds] = useState<{
    maxLat: number;
    minLat: number;
    maxLng: number;
    minLng: number;
  } | null>(null);

  const [submarketPolygons, setSubmarketPolygons] = useState<any[]>([]);
  const [infoWindow, setInfoWindow] = useState<any>(null);
  const hasInitialZoomedRef = useRef<boolean>(false);
  const prevHeatmapModeRef = useRef<string | null>(null);
  const topRightUIGroupRef = useRef<HTMLDivElement | null>(null);
  const poiOverlaysRef = useRef<any[]>([]);
  const hasHotspotZoomedRef = useRef<boolean>(false);

  const [selectedSubmarket, setSelectedSubmarket] = useState<any>(null);
  const [hideConfirmOpen, setHideConfirmOpen] = useState(false);
  const [shouldShowSubmarketMarkers, setShouldShowSubmarketMarkers] = useState(false);
  const [clusters, setClusters] = useState<any[]>([]);
  const [clustersLoading, setClustersLoading] = useState(true);
  const [visibleMapClusters, setVisibleMapClusters] = useState<any[]>([]);
  const [shouldShowClusters, setShouldShowClusters] = useState(
    dashboardLayoutView === 'submarkets' ? true : false
  );

  const clusterKey = useMemo(() => {
    if (dashboardLayoutView === 'map') {
      return 'supply';
    }

    if (
      heatmapMode === 'smartZones' &&
      ['yoy_revenue_change', 'neighborhood_grade'].includes(submarketSortKey)
    ) {
      return 'supply';
    }

    if (submarketSortKey === 'occupancy_rate') {
      return 'occupancyRate';
    }

    return submarketSortKey || 'supply';
  }, [heatmapMode, submarketSortKey]);

  const [hasClickedCluster, setHasClickedCluster] = useState(false);
  const [lastZoomLevel, setLastZoomLevel] = useState<number | null>(null);

  const [drawerHeight, setDrawerHeight] = useState(MIN_DRAWER_HEIGHT);
  const [collapsed, setCollapsed] = useState(true);

  const [drawingManagerCopy, setDrawingManagerCopy] = useState<any>(null);
  const [topRightUIGroupHeight, setTopRightUIGroupHeight] = useState<number>(35);

  const sortedPois = useMemo(() => sortPois(pois || []), [pois]);
  const hasPois = sortedPois.length > 0;

  const heatmapModeOptions = {
    ...(hasPois && { hotspot: 'Hotspot Analysis' }),
    smartZones: 'Smart Zones',
    submarkets: 'Submarkets',
  };

  const clusterMetricOptions: any = {
    supply: 'Active Listings',
    adr: 'Nightly Rate',
    neighborhood_grade: 'Grade',
    yoy_revenue_change: 'Up & Coming',
    occupancy_rate: 'Occupancy Rate',
  };

  const isClusterHeatmapActive = useMemo(() => {
    return clusters.length > 0;
  }, [clusters]);

  // toggles map center from different tabs
  useEffect(() => {
    setMapCenter({ lat: search.data.lat, lng: search.data.lng });
  }, [dashboardLayoutView]);


  useEffect(() => {
    if (!search.loading && bounds) {
      let newClusters: any[] = [];
      if (!backendComputedClusters.loading && backendComputedClusters.data.length > 0) {
        newClusters = backendComputedClusters.data;
        setClusters(newClusters);
        setVisibleMapClusters(newClusters);
        setClustersLoading(false);
      }
    }
  }, [useFullMarkers, search, filterActive, bounds, backendComputedClusters]);

  const getColors = (percentiles, value, isFaded = false) => {
    // > 75th percentile: COLOR_PRIMARY_2
    // 50th - 75th percentile: COLOR_PRIMARY_2_600
    // 25th - 50th percentile: COLOR_PRIMARY_2_400
    // < 25th percentile: COLOR_PRIMARY_2_200

    if (isFaded) {
      return COLOR_PRIMARY_2_100;
    }

    if (value === null) {
      return COLOR_PRIMARY_2_100;
    }

    if (value > percentiles[2]) {
      return COLOR_PRIMARY_2;
    } else if (value > percentiles[1]) {
      return COLOR_PRIMARY_2_700;
    } else if (value > percentiles[0]) {
      return COLOR_PRIMARY_2_500;
    } else {
      return COLOR_PRIMARY_2_300;
    }
  };

  useEffect(() => {
    if (mapMode == 'clusters') {
      setHeatmapMode('smartZones');
    }
  }, [mapMode]);

  const clusterMarkers = useMemo(() => {
    if (!clusters || !bounds || Object.keys(bounds).length === 0) return null;

    // Calculate percentiles for coloring
    const dataArray = clusters.map(cluster => cluster[clusterKey]);
    const [percentile25, percentile50, percentile75] = calculatePercentiles(dataArray, [
      0.25,
      0.5,
      0.75,
    ]);

    const [unit, location] = getLegendUnit(clusterKey, currency);

    let breaks = [percentile25, percentile50, percentile75];
    if (clusterKey == 'adr' || clusterKey == 'revPar') {
      breaks = [
        convertCurrency(currency, percentile25),
        convertCurrency(currency, percentile50),
        convertCurrency(currency, percentile75),
      ];
    }
    const legends = generateChoroplethLegends(clusterKey, breaks, unit, location);
    setClusterChoroplethLegends(legends);

    const boundedClusters = getBoundedClusters(clusters, bounds);

    if (boundedClusters.length === 0) return null;

    const gridSize = 5;
    const minClustersPerCell = 1;
    const maxClustersToShow = matches ? 10 : 30;
    const numberOfHiddenClustersToShow = 10;

    // Create a n x n grid
    const latStep = (bounds.northEast.lat - bounds.southWest.lat) / gridSize;
    const lngStep = (bounds.northEast.lng - bounds.southWest.lng) / gridSize;

    // Initialize grid cells
    const grid: any[][] = Array(gridSize)
      .fill(null)
      .map(() =>
        Array(gridSize)
          .fill(null)
          .map(() => [])
      );

    // Sort clusters by performance
    const sortedClusters = boundedClusters.sort((a, b) => {
      return b[clusterKey] - a[clusterKey];
    });

    // Assign clusters to grid cells
    sortedClusters.forEach(cluster => {
      const centroid = cluster.centroid;
      const latIndex = Math.min(
        Math.floor((centroid.latitude - bounds.southWest.lat) / latStep),
        gridSize - 1
      );
      const lngIndex = Math.min(
        Math.floor((centroid.longitude - bounds.southWest.lng) / lngStep),
        gridSize - 1
      );
      grid[latIndex][lngIndex].push(cluster);
    });

    // Select top clusters from each grid cell
    const selectedClusters: any[] = [];

    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const cellClusters = grid[i][j];
        if (cellClusters.length > 0) {
          // Take top clusters from this cell
          selectedClusters.push(...cellClusters.slice(0, minClustersPerCell));
        }
      }
    }

    // If we have fewer clusters than needed, add more from the sorted list
    if (selectedClusters.length < maxClustersToShow) {
      const remainingClusters = sortedClusters.filter(
        cluster => !selectedClusters.includes(cluster)
      );
      selectedClusters.push(
        ...remainingClusters.slice(
          0,
          maxClustersToShow + numberOfHiddenClustersToShow - selectedClusters.length
        )
      );
    }

    return selectedClusters.map((cluster, index) => {
      const centroid = cluster.centroid;
      return (
        <ClusterPointer
          marketID={marketID}
          key={cluster.cluster_id}
          lat={centroid.latitude}
          lng={centroid.longitude}
          supply={cluster.supply}
          performanceMetrics={{
            adr: cluster.adr,
            occupancyRate: cluster.occupancyRate,
            revPar: cluster.revPar,
            supply: cluster.supply,
            revenue: cluster.revenue,
          }}
          clusterKey={clusterKey}
          currency={currency}
          color={getColors(
            [percentile25, percentile50, percentile75],
            cluster[clusterKey],
            index >= maxClustersToShow
          )}
          shouldFade={index >= maxClustersToShow}
          savedClusters={savedClusters}
          setSavedClusters={setSavedClusters}
        />
      );
    });
  }, [
    clusters,
    clusterKey,
    currency,
    matches,
    bounds,
    mapCopy,
    mapsCopy,
    savedClusters,
    mapMode,
    heatmapMode,
  ]);

  useEffect(() => {
    if (hoveredClusterId) {
      const hoveredCluster = clusters.find(cluster => cluster.cluster_id === hoveredClusterId);
      hoveredCluster && !matches && handleClusterHover(hoveredCluster);
    } else {
      handleClusterHoverOut();
    }
  }, [hoveredClusterId, clusters, matches]);

  useEffect(() => {
    if (topPerformingMarketOnFocus) {
      let center = topPerformingMarketOnFocus.center;
      const { latitude, longitude } = extractLatLong(center);
      setMapCenter({ lat: latitude, lng: longitude });
    }
  }, [topPerformingMarketOnFocus]);

  const updateMarketDetailsPremiumDataVisibility = (bounds: Bounds, isUserAllowed: boolean) => {
    const { isInside: isPolygonInsideOfBounds } = comparePolygonLimitsToBoundary(
      polygonBounds,
      bounds,
      20
    );

    if (!isUserAllowed && !isPolygonInsideOfBounds) {
      setShowPremiumDataDynamic(false);
    } else if (isPolygonInsideOfBounds) {
      setShowPremiumDataDynamic(true);
    }
  };

  const handleMapChange = async ({ center, bounds, zoom }: GoogleMapReact.ChangeEventValue) => {
    if (isTabChanging) {
      return;
    }

    if (isMarketTab) {
      setMapLoading(true);
      showViewingLisingFromPaginationAlert === 'showing' &&
        setShowViewingLisingFromPaginationAlert('shown');

      visibleMapListings && visibleMapListings.length > 0 && setIsDynamicDataBtnVisible(true);
      const boundsObj = formatGoogleMapsBounds(bounds);
      setBounds(boundsObj);
      setZoom(zoom);

      const { filters } =
        filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed
          ? { filters: {} }
          : parseFilterValToApiResponse(filterVal);
      dispatch(
        fetchVisibleMapSupply(
          marketID,
          search.data.polygon || search.data.multiPolygon,
          boundsObj,
          search.data.isSubmarket ? {} : filters,
          listingSortingKey
        )
      );
      dispatch(
        getMapOverviewKpi(
          marketID,
          search.data.polygon || search.data.multiPolygon,
          boundsObj,
          filters
        )
      );

      const polygonArray: [number, number][] = [
        [boundsObj.northEast.lat, boundsObj.northEast.lng], // Top-right
        [boundsObj.southWest.lat, boundsObj.northEast.lng], // Bottom-right
        [boundsObj.southWest.lat, boundsObj.southWest.lng], // Bottom-left
        [boundsObj.northEast.lat, boundsObj.southWest.lng], // Top-left
        [boundsObj.northEast.lat, boundsObj.northEast.lng], // Close the polygon (back to top-right)
      ];
      setSubmarketPolygonPaths(polygonArray);

      await callDynamicMarkers(marketID, boundsObj, filterVal);
      await callBookingMarkers(marketID, boundsObj);

      setListingsGridStartIndexOffset(_ => 0);
      polygonBounds && updateMarketDetailsPremiumDataVisibility(boundsObj, isUserAllowed);

      dispatch(getMarketsInMapViewport(boundsObj));

      setTimeout(() => {
        setMapLoading(false);
      }, 500);
    }
  };

  const isBoundLoaded = !isEmpty(bounds);

  useEffect(() => {
    if (isBoundLoaded) {
      isBoundLoaded && callDynamicMarkers(marketID, bounds, filterVal);
      const { filters } =
        filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed
          ? { filters: {} }
          : parseFilterValToApiResponse(filterVal);
      dispatch(
        fetchVisibleMapSupply(
          marketID,
          search.data.polygon || search.data.multiPolygon,
          bounds,
          search.data.isSubmarket ? {} : filters,
          listingSortingKey
        )
      );
      dispatch(
        getMapOverviewKpi(
          marketID,
          search.data.polygon || search.data.multiPolygon,
          bounds,
          filters
        )
      );
    }
  }, [filterVal, isBoundLoaded]);

  const setPolygon = (
    map,
    maps,
    paths: Coordinates[][],
    strokeColor: string,
    fillColor: string,
    strokeOpacity = 0.8,
    strokeWeight = 2,
    fillOpacity = 0.12,
    submarketId?: string,
    submarketData?: any,
    zIndex = 1
  ) => {
    const polygon = new maps.Polygon({
      paths: paths,
      strokeColor: strokeColor,
      strokeOpacity: strokeOpacity,
      strokeWeight: strokeWeight,
      fillColor: fillColor,
      fillOpacity: fillOpacity,
      zIndex: zIndex,
    });
    polygon.setMap(map);

    if (submarketId) {
      maps.event.addListener(polygon, 'mouseover', e => {
        polygon.setOptions({
          strokeColor: COLOR_PRIMARY_2,
          fillColor: COLOR_PRIMARY_2,
          fillOpacity: 0.4,
          strokeWeight: 3,
          zIndex: 10,
        });
        submarketId && setHoveredSubmarketId(submarketId);
        e && e.latLng && setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        setHoveredSubmarketData(submarketData);
        setHoveredClusterData(submarketData);
      });

      maps.event.addListener(polygon, 'mousemove', e => {
        setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });

      maps.event.addListener(polygon, 'mouseout', () => {
        polygon.setOptions({
          fillOpacity: fillOpacity,
          strokeWeight: strokeWeight,
          zIndex: zIndex,
        });
        setHoveredSubmarketId(null);
        setTooltipPosition(null);
        setHoveredSubmarketData(null);
      });

      maps.event.addListener(polygon, 'click', e => {
        setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        setHoveredSubmarketData(submarketData);
        setHoveredClusterData(submarketData);
      });
    }

    if (isMarketTab) {
      // set dashboard to map view on interaction with map/polygons
      maps.event.addListener(polygon, 'click', function(e) {
        setDashboardLayoutView(e => {
          if (e == 'dashboard') {
            return 'map';
          }
          return e;
        });
      });

      maps.event.addListener(map, 'dragend', function(e) {
        setDashboardLayoutView(e => {
          if (e == 'dashboard') {
            return 'map';
          }
          return e;
        });
      });
    }

    return polygon;
  };

  const apiIsLoaded = (
    map,
    maps,
    polygonRaw: string,
    multiPolygonRaw: string,
    boundary: string
  ) => {
    const setMarketPolygon = () => {
      const paths = getPolygonPaths(polygonRaw, multiPolygonRaw, boundary);
      setPolygonPath(paths);
      const polygon = setPolygon(
        map,
        maps,
        paths,
        COLOR_PRIMARY_DARK,
        MAP_FILL_COLOR,
        0.8,
        2,
        0.12,
        undefined,
        undefined,
        1
      );
      applySmartBoundsForCoords(map, maps, paths);
      setSearchPolygonCopy(polygon);
      setPolygonBounds(calculateBounds(paths));
    };

    const setParentPolygon = () => {
      if (
        !parentMarketDetails ||
        (!parentMarketDetails.polygon && !parentMarketDetails.multiPolygon)
      ) {
        return;
      }

      const paths = getPolygonPaths(
        parentMarketDetails.polygon,
        parentMarketDetails.multiPolygon,
        parentMarketDetails.boundary
      );
      const polygon = setPolygon(
        map,
        maps,
        paths,
        COLOR_PRIMARY_DARK + '00',
        MAP_FILL_COLOR + '00'
      );
      setParentPolygonCopy(polygon);
      setPolygonBounds(calculateBounds(paths));
      applySmartBoundsForCoords(map, maps, paths);
    };

    if (isMarketTab) {
      setMarketPolygon();
      callDynamicMarkers(marketID, bounds, filterVal);
    } else if (dashboardLayoutView === 'submarkets') {
      setMarketPolygon();
      setParentPolygon();
    }

    setMapCopy(map);
    setMapsCopy(maps);
  };

  useEffect(() => {
    if (isMarketTab && mapCopy && mapsCopy && search.data) {
      searchPolygonCopy && searchPolygonCopy.setMap(null);

      setMapCenter({ lat: search.data.lat, lng: search.data.lng });

      const paths = getPolygonPaths(
        search.data.polygon,
        search.data.multiPolygon,
        search.data.boundary
      );
      const polygon = setPolygon(mapCopy, mapsCopy, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
      applySmartBoundsForCoords(mapCopy, mapsCopy, paths);
      setSearchPolygonCopy(polygon);
      setPolygonBounds(calculateBounds(paths));
    } else {
      searchPolygonCopy && searchPolygonCopy.setMap(null);
    }
  }, [dashboardLayoutView, marketsToCompareList]);

  useEffect(() => {
    if (mapsCopy) {
      setInfoWindow(new mapsCopy.InfoWindow());
    }
  }, [mapsCopy]);

  const cancelDrawing = () => {
    renderHeatmap(null, false);
    mapCopy &&
      mapCopy.setOptions({
        draggable: true,
        scrollwheel: true,
        gestureHandling: 'auto',
        disableDoubleClickZoom: false,
        clickableIcons: true,
        keyboardShortcuts: true,
        zoomControl: true,
        draggableCursor: 'default',
      });
  };

  useEffect(() => {
    if (!isNewSubmarketDrawingMode) {
      cancelDrawing();
    }
  }, [isNewSubmarketDrawingMode]);

  const markers = useMemo(() => {
    if (visibleMapListings && visibleMapListings.length) {
      let revenueMarkerCount = 0;
      return visibleMapListings.map((listing: Listing, index: number) => {
        const item = listing;
        if (index % 2 == 0) {
          revenueMarkerCount = revenueMarkerCount + 1;
        }
        const bedroom = listing?.bedrooms?.toLowerCase() == 'studio' ? 0 : listing?.bedrooms;
        const referenceADR = cachedMarketBedroomADR ? cachedMarketBedroomADR[bedroom] : null;

        return (
          <Pointer
            key={index + '-map-pointer'}
            lat={item.latitude}
            lng={item.longitude}
            setSubscriptionModalOpen={setSubscriptionModalOpen}
            data={item}
            canDelete={true}
            isDisabled={isDisabled}
            currency={currency}
            subscriptionModalOpen={subscriptionModalOpen}
            market={search.data.name}
            handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'individualListingsKPI')}
            user={user}
            topPerformingListingOnFocus={topPerformingListingOnFocus}
            isEstimator={false}
            openDemoBookingForm={openDemoBookingForm}
            setDemoFormHeadingFor={setDemoFormHeadingFor}
            showRevenuePotential={index % 2 == 0 && revenueMarkerCount <= 10 ? true : false}
            referenceADR={referenceADR}
            cachedMarketData={cachedMarketData}
            dataKey={clusterKey == 'supply' ? 'adr' : clusterKey}
            currentHoveredCardFromDataGrid={currentHoveredCardFromDataGrid}
            marketID={marketID}
          />
        );
      });
    }
    return <></>;
  }, [
    visibleMapListings,
    currency,
    topPerformingListingOnFocus,
    clusterKey,
    currentHoveredCardFromDataGrid,
    isNewSubmarketDrawingMode,
  ]);

  const bookingMarkers = useMemo(() => {
    if (visibleBookingListings && visibleBookingListings.length && !filterApplied) {
      const numberOfMarkersToShow =
        visibleMapListings.length >= 9 ? 2 : 11 - visibleMapListings.length;
      return visibleBookingListings
        .slice(0, numberOfMarkersToShow)
        .map((listing: Listing, index: number) => {
          const item = listing;
          const bedroom = listing?.bedrooms?.toLowerCase() == 'studio' ? 0 : listing?.bedrooms;
          const referenceADR = cachedMarketBedroomADR ? cachedMarketBedroomADR[bedroom] : null;

          return (
            <Pointer
              key={index + '-map-pointer'}
              lat={item.latitude}
              lng={item.longitude}
              setSubscriptionModalOpen={setSubscriptionModalOpen}
              data={item}
              canDelete={true}
              isDisabled={isDisabled}
              currency={currency}
              subscriptionModalOpen={subscriptionModalOpen}
              market={search.data.name}
              handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'individualListingsKPI')}
              user={user}
              topPerformingListingOnFocus={topPerformingListingOnFocus}
              isEstimator={false}
              openDemoBookingForm={openDemoBookingForm}
              setDemoFormHeadingFor={setDemoFormHeadingFor}
              showRevenuePotential={true}
              referenceADR={referenceADR}
              cachedMarketData={cachedMarketData}
              dataKey={clusterKey == 'supply' ? 'adr' : clusterKey}
              currentHoveredCardFromDataGrid={currentHoveredCardFromDataGrid}
              isBookingComListing={true}
              marketID={marketID}
            />
          );
        });
    }
    return <></>;
  }, [
    visibleBookingListings,
    visibleMapListings,
    currency,
    topPerformingListingOnFocus,
    clusterKey,
    currentHoveredCardFromDataGrid,
    isNewSubmarketDrawingMode,
    filterApplied,
  ]);

  useEffect(() => {
    if (
      dashboardLayoutView == 'map' &&
      parentMarketDetails &&
      submarkets != 'loading' &&
      submarkets &&
      submarkets.length > 0
    ) {
      setShouldShowSubmarketMarkers(true);
    } else {
      setShouldShowSubmarketMarkers(false);
    }
  }, [dashboardLayoutView, submarkets, parentMarketDetails]);

  const submarketMarkers = useMemo(() => {
    if (!submarkets || !Array.isArray(submarkets) || isNewSubmarketDrawingMode) return <></>;

    if (shouldShowSubmarketMarkers && bounds && Object.keys(bounds).length > 0) {
      let submarkers = submarkets.filter(submarket => {
        const googleMapsBounds = {
          sw: { lat: bounds.southWest.lat, lng: bounds.southWest.lng },
          ne: { lat: bounds.northEast.lat, lng: bounds.northEast.lng },
        };
        return (
          isPointWithinBounds(submarket.center, googleMapsBounds as GoogleMapReact.Bounds) &&
          submarket.submarket_name !== search.data.name
        );
      });

      if (submarkers.length > 3) {
        submarkers = submarkers.slice(0, 3);
      }

      return submarkers.map((submarket: Submarket, index: number) => (
        <SubmarketPointer
          key={index + '-submarket-pointer'}
          lat={submarket.center.lat}
          lng={submarket.center.lng}
          submarket={submarket}
          currency={currency}
          userhasDataAccess={isUserAllowed}
          handleOnSampleClick={handleOnSampleClick}
        />
      ));
    }
    return <></>;
  }, [shouldShowSubmarketMarkers, submarkets, bounds]);

  const marketInMapViewportMarkers = useMemo(() => {
    const maxMarketsToShow = matches ? 3 : isFullOverviewMap ? 20 : 7;
    if (!marketsInMapViewport.data) {
      return <></>;
    }
    return marketsInMapViewport.data
      .slice(0, maxMarketsToShow)
      .map((market: MarketInMapViewport, index: number) => {
        // don't show the current market in the map viewport
        if (market.market_id === search.data?.id) {
          return <></>;
        }
        return (
          <MarketPointer
            key={index + '-market-in-map-viewport-pointer'}
            lat={market.latitude}
            lng={market.longitude}
            name={market.name}
            value={`${abbreviateNumber(market.supply)} listings`}
            url={market.url}
          />
        );
      });
  }, [marketsInMapViewport, matches, isFullOverviewMap]);

  const isDrawnPolygonInsideParent = (
    drawnPath: google.maps.LatLngLiteral[], // [{lat, lng}, ...]
    parentWkt: string, // parent polygon WKT
    isMultiPolygon: boolean // true if parent is multipolygon
  ): boolean => {
    if (!drawnPath || drawnPath.length < 3 || !parentWkt) return false;

    try {
      // 1. Close ring for drawn polygon
      const closedPath = [...drawnPath, drawnPath[0]];

      // 2. Convert drawn polygon → turf polygon
      const drawnPolygon = turf.polygon([closedPath.map(p => [p.lng, p.lat])]);

      // 3. Convert WKT → coordinates
      const boundaryCoords = parseWKTStringToCoordinates(parentWkt, isMultiPolygon, true);

      // 4. Wrap into turf polygon/multipolygon
      const parentPolygon = isMultiPolygon
        ? turf.multiPolygon(boundaryCoords as any)
        : turf.polygon(boundaryCoords as any);

      // 5. Check containment
      return turf.booleanWithin(drawnPolygon, parentPolygon);
    } catch (err) {
      console.log('Error checking polygon containment:', err);
      return false;
    }
  };

  const enableFreehandDrawing = (
    map: google.maps.Map,
    maps: typeof google.maps,
    isDrawingMode: boolean = false
  ) => {
    if (!map || !maps || !isDrawingMode) return;

    if (!('drawing' in maps)) {
      console.warn('[enableFreehandDrawing] Google Maps drawing library not loaded.');
      return;
    }

    maps.event.clearListeners(map, 'mousedown');
    maps.event.clearListeners(map, 'mousemove');
    maps.event.clearListeners(map, 'mouseup');
    maps.event.clearListeners(map, 'touchstart');
    maps.event.clearListeners(map, 'touchmove');
    maps.event.clearListeners(map, 'touchend');

    map.setOptions({
      draggable: false,
      gestureHandling: 'none',
      scrollwheel: false,
      disableDoubleClickZoom: true,
      clickableIcons: false,
      keyboardShortcuts: false,
      zoomControl: false,
    });

    const drawingManager = new maps.drawing.DrawingManager({
      drawingMode: maps.drawing.OverlayType.POLYGON,
      drawingControl: false,
      polygonOptions: {
        clickable: false,
        editable: true,
        draggable: false,
        strokeColor: COLOR_PRIMARY_2,
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: COLOR_PRIMARY_2,
        fillOpacity: 0.35,
        zIndex: 9999999,
      },
    });
    setDrawingManagerCopy(drawingManager);

    drawingManager.setMap(map);

    const resumeAndCleanup = () => {
      map.setOptions({
        draggable: true,
        scrollwheel: true,
        gestureHandling: 'auto',
        disableDoubleClickZoom: false,
        clickableIcons: true,
        keyboardShortcuts: true,
        zoomControl: true,
      });

      drawingManager.setDrawingMode(null as any);
      drawingManager.setMap(null);
    };

    const polygonCompleteListener = maps.event.addListener(
      drawingManager,
      'polygoncomplete',
      (polygon: google.maps.Polygon) => {
        drawingManager.setDrawingMode(null as any);

        const path: google.maps.LatLngLiteral[] = polygon
          .getPath()
          .getArray()
          .map((p: google.maps.LatLng) => ({ lat: p.lat(), lng: p.lng() }));

        if (path.length > 2) {
          const first = path[0];
          const last = path[path.length - 1];
          if (first.lat !== last.lat || first.lng !== last.lng) {
            path.push({ ...first });
          }
        }

        const referenceMarketBoundaryToCheck = parentMarketDetails
          ? parentMarketDetails.polygon || parentMarketDetails.multiPolygon
          : search.data.polygon || search.data.multiPolygon;

        const isMultiPolygon = parentMarketDetails
          ? parentMarketDetails.multiPolygon
          : search.data.multiPolygon;

        const inside = isDrawnPolygonInsideParent(
          path,
          referenceMarketBoundaryToCheck,
          isMultiPolygon
        );

        if (!inside) {
          polygon.setMap(null);
          setSubmarketDrawnOutOfParent(true);
        } else {
          setTailoredSubmarketPolygon(path);
          setShowSubmarketCreationModal(true);
        }

        setIsNewSubmarketDrawingMode(false);
        renderHeatmap(null, false);

        maps.event.clearInstanceListeners(polygon.getPath());

        resumeAndCleanup();
      }
    );
  };

  const disableAllPolygonInteractions = (
    polygons: google.maps.Polygon[],
    maps: typeof google.maps
  ) => {
    polygons.forEach(polygon => {
      maps.event.clearInstanceListeners(polygon);

      polygon.setOptions({
        clickable: false,
        fillOpacity: 0.2,
        strokeOpacity: 0.2,
        zIndex: -99,
      });
    });
  };

  const MapDrawButton = (
    <TooltipNew
      content={
        <Box sx={{ width: '600px', height: 'auto', borderRadius: '4px' }}>
          <img
            src={drawSubmarketGif}
            alt="How to draw custom submarket"
            style={{
              width: '600px',
              height: 'auto',
              borderRadius: '4px',
            }}
          />
        </Box>
      }
      title={''}
      placement="right"
      arrow
    >
      <Button
        variant="outlined"
        size="small"
        onMouseEnter={() => {
          setHoveredSubmarketId(_ => null);
        }}
        onClick={() => {
          if (!isLoggedIn) {
            handleOnSampleClick('expert');
            return;
          }

          if (isNewSubmarketDrawingMode) {
            parentPolygonCopy &&
              parentPolygonCopy.setOptions({
                clickable: false,
                fillOpacity: 0,
                strokeOpacity: 0,
                zIndex: 0,
                strokeColor: COLOR_PRIMARY_DARK,
              });
            setIsNewSubmarketDrawingMode(false);
            drawingManagerCopy && drawingManagerCopy.setMap(null);
            return;
          }

          mapCopy &&
            mapCopy.setOptions({
              draggableCursor: 'crosshair',
            });

          setIsNewSubmarketDrawingMode(true);
          disableAllPolygonInteractions([...submarketPolygons, searchPolygonCopy], mapsCopy);

          // Show the parent boundary
          parentPolygonCopy
            ? parentPolygonCopy.setOptions({
                clickable: false,
                fillOpacity: 0,
                strokeOpacity: 0.8,
                zIndex: 0,
                strokeColor: COLOR_PRIMARY_DARK,
              })
            : searchPolygonCopy &&
              searchPolygonCopy.setOptions({
                clickable: false,
                fillOpacity: 0.2,
                strokeOpacity: 0.8,
                zIndex: 0,
                strokeColor: COLOR_PRIMARY_DARK,
              });

          enableFreehandDrawing(mapCopy, mapsCopy, true);
        }}
        sx={{
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: COLOR_PRIMARY_2,
            color: 'white',
          },
          color: COLOR_PRIMARY_2,
          borderColor: COLOR_PRIMARY_2,
        }}
      >
        {isNewSubmarketDrawingMode ? <CancelIcon sx={{ mr: 1 }} /> : <DrawIcon sx={{ mr: 1 }} />}
        {isNewSubmarketDrawingMode ? 'Cancel' : 'Add Custom Submarket'}
      </Button>
    </TooltipNew>
  );

  const resetAllPolygons = () => {
    setSubmarketPolygons(prev => {
      prev.forEach(polygon => {
        polygon && polygon.setMap(null);
      });
      return [];
    });

    hoveredClusterPolygon && hoveredClusterPolygon.setMap(null);
    setHoveredClusterPolygon(null);

    setHoveredClusterData(null);
    setHoveredSubmarketData(null);
    setTooltipPosition(null);
  };

  const handleClusterHover = submarket => {
    setHoveredClusterData(submarket);
  };

  const handleClusterHoverOut = () => {
    setHoveredClusterData(null);
  };

  const renderHeatmap = _.debounce(
    (hoveredSubmarketId: string | null, isDrawPolygonMode: boolean = false) => {
      if (isNewSubmarketDrawingMode) {
        handleClusterHoverOut();
        return;
      }

      resetAllPolygons();

      // Hotspot mode draws POI pins + circles in a dedicated effect; skip the choropleth.
      if (heatmapMode === 'hotspot') {
        setNeighbourhoodChoroplethLegends({});
        return;
      }

      if (!clusters || !mapCopy || !mapsCopy) return;

      let heatmapSubmarkets: any[] = [];
      if (heatmapMode === 'smartZones' && dashboardLayoutView === 'submarkets') {
        resetAllPolygons();
        if (!Array.isArray(clusters)) return;

        heatmapSubmarkets = clusters
          .map((cluster, index) => {
            if (!cluster || !cluster.coordinates || !Array.isArray(cluster.coordinates)) {
              return null;
            }

            // Convert coordinates array to path format
            const hexagonPath = cluster.coordinates
              .map(coord => {
                if (!Array.isArray(coord) || coord.length < 2) return null;
                return {
                  lng: coord[0],
                  lat: coord[1],
                };
              })
              .filter(Boolean); // Remove null entries

            if (hexagonPath.length === 0) return null;

            return {
              ...cluster,
              submarketId: cluster.cluster_id,
              path: [hexagonPath],
              occupancy_rate: cluster.occupancyRate || cluster.occupancy_rate || 0,
              supply: cluster.supply || 0,
              revenue: (cluster.revPar || 0) * 365,
              centroid: cluster.centroid || { latitude: 0, longitude: 0 },
              cluster_id: cluster.cluster_id,
              neighborhood_grade: null,
              yoy_revenue_change: null,
              adr: cluster.adr || 0,
            };
          })
          .filter(Boolean) as any[]; // Remove null entries
      } else {
        if (!submarkets || !Array.isArray(submarkets)) return;

        heatmapSubmarkets = submarkets
          .map(submarket => {
            if (!submarket || !submarket.boundary) return null;

            if (
              submarket?.submarket_id &&
              hiddenSubmarkets?.data &&
              hiddenSubmarkets.data.includes(String(submarket.submarket_id))
            )
              return null;

            const wktBoundaryString = submarket.boundary;
            const neighbourhoodCoordinates = getCoordinatesFromWKT(wktBoundaryString);

            if (!neighbourhoodCoordinates || !Array.isArray(neighbourhoodCoordinates)) return null;

            const isHovered =
              (heatmapMode == 'submarkets' || mapMode == 'clusters') &&
              (submarket.submarketId || submarket.submarket_id) == hoveredSubmarketId;

            return {
              ...submarket,
              submarketId: submarket.submarketId || submarket.submarket_id,
              path: neighbourhoodCoordinates,
              occupancy_rate: submarket.occupancy || submarket.occupancy_rate || 0,
              supply: submarket.supply || 0,
              revenue: submarket.revenue || 0,
              adr: submarket.adr || 0,
              yoy_revenue_change: submarket.yoy_revenue_change || 0,
              neighborhood_grade: submarket.neighborhood_grade || null,
              centroid: {
                latitude: submarket.center?.lat || 0,
                longitude: submarket.center?.lng || 0,
              },
              coordinates: neighbourhoodCoordinates,
              ...(isHovered && {
                color: COLOR_PRIMARY_2,
                isHovered: true,
              }),
            };
          })
          .filter(Boolean) as any[]; // Remove null entries
      }

      // Early return if no valid submarkets
      if (!heatmapSubmarkets || heatmapSubmarkets.length === 0) {
        setNeighbourhoodChoroplethLegends({});
        resetAllPolygons();
        return;
      }

      const validSubmarketMetrics = heatmapSubmarkets
        .filter(
          submarket =>
            submarket[submarketSortKey] !== undefined &&
            !Number.isNaN(submarket[submarketSortKey]) &&
            submarket[submarketSortKey] !== null
        )
        .map(submarket => submarket[submarketSortKey]);

      let choroplethBreaks: any[] = [];
      if (submarketSortKey === 'neighborhood_grade') {
        choroplethBreaks = ['A', 'B', 'C', 'D', 'E', 'F'];
      } else {
        choroplethBreaks = getNaturalBreaks(
          validSubmarketMetrics,
          ['revenue', 'adr'].includes(submarketSortKey) ? currency : null
        );
      }

      // Ensure we have valid breaks
      if (!choroplethBreaks || choroplethBreaks.length === 0) {
        setNeighbourhoodChoroplethLegends({});
        resetAllPolygons();
        return;
      }

      const [unit, location] = getLegendUnit(submarketSortKey, currency);

      // Create legends based on available breaks
      const legends = generateChoroplethLegends(submarketSortKey, choroplethBreaks, unit, location);
      setNeighbourhoodChoroplethLegends(legends);

      let submarketPolygons: any[] = [];

      heatmapSubmarkets.forEach((submarket, index) => {
        if (
          !submarket ||
          !submarket.path ||
          !Array.isArray(submarket.path) ||
          submarket.path.length === 0
        ) {
          return; // Skip invalid submarkets
        }

        const submarketKey = submarket[submarketSortKey];
        const color = submarket.isHovered
          ? COLOR_PRIMARY_2
          : submarketSortKey === 'neighborhood_grade'
          ? getGradeColor(submarketKey || '')
          : getSubmarketColor(submarketKey, choroplethBreaks, submarketSortKey);

        const zIndexValue = submarket.isHovered ? 1000 : 5 + index * 0.01;

        const submarketPolygon = setPolygon(
          mapCopy,
          mapsCopy,
          submarket.path,
          heatmapMode == 'submarkets' || submarket.isHovered ? COLOR_PRIMARY_2 : color,
          color,
          heatmapMode == 'submarkets' ? 0.8 : 0, // strokeOpacity
          heatmapMode == 'submarkets' ? 2 : 0, // strokeWeight
          shouldShowClusters || submarket.isHovered ? 0.8 : 0.2, // fillOpacity
          submarket.isHovered ? submarket.submarket_id : undefined, // submarketId
          submarket, // submarketData
          zIndexValue // zIndex
        );

        if (!submarketPolygon) return; // Skip if polygon creation failed

        // Add hover event listeners
        mapsCopy.event.addListener(submarketPolygon, 'mouseover', e => {
          if (isDrawPolygonMode) return;
          if (submarketPolygon.setOptions) {
            submarketPolygon.setOptions({
              strokeWeight: 3,
              strokeOpacity: 0.8,
              strokeColor: COLOR_PRIMARY_2,
              fillOpacity: 0.6,
              fillColor: COLOR_PRIMARY_2,
              zIndex: 100,
            });
            !matches && handleClusterHover(submarket);
            (submarket.submarket_id || submarket.submarketId) &&
              setHoveredSubmarketId(submarket.submarket_id || submarket.submarketId);
            e && e.latLng && setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
            setHoveredSubmarketData(submarket);
            setHoveredClusterData(submarket);
          }
        });

        mapsCopy.event.addListener(submarketPolygon, 'mouseout', () => {
          if (isDrawPolygonMode) return;
          if (submarketPolygon && submarketPolygon.setOptions) {
            submarketPolygon.setOptions({
              strokeWeight: heatmapMode == 'submarkets' ? 2 : 0,
              strokeOpacity: heatmapMode == 'submarkets' ? 0.8 : 0,
              strokeColor: heatmapMode == 'submarkets' ? COLOR_PRIMARY_2 : color,
              fillOpacity: shouldShowClusters ? 0.8 : 0.2,
              zIndex: zIndexValue,
            });
            handleClusterHoverOut();
          } else {
            setHoveredSubmarketId(null);
            setTooltipPosition(null);
            setHoveredSubmarketData(null);
          }
        });

        mapsCopy.event.addListener(submarketPolygon, 'click', e => {
          if (isDrawPolygonMode) return;
          handleClusterHover(submarket);
          submarket.submarket_id && setHoveredSubmarketId(submarket.submarket_id);
          setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
          setHoveredSubmarketData(submarket);
          setHoveredClusterData(submarket);
        });

        !matches &&
          !isDrawPolygonMode &&
          mapsCopy.event.addListener(submarketPolygon, 'rightclick', e => {
            setRightClickedCluster(submarket);
            return false;
          });

        matches &&
          !isDrawPolygonMode &&
          mapsCopy.event.addListener(submarketPolygon, 'contextmenu', e => {
            setRightClickedCluster(submarket);
            return false;
          });

        submarketPolygons.push(submarketPolygon);
      });

      if (!hasInitialZoomedRef.current) {
        const bounds = new mapsCopy.LatLngBounds();
        heatmapSubmarkets.forEach((submarket: any) => {
          if (submarket.centroid.latitude && submarket.centroid.longitude) {
            bounds.extend(
              new mapsCopy.LatLng(submarket.centroid.latitude, submarket.centroid.longitude)
            );
          }
        });
        mapCopy.fitBounds(bounds, { top: 0, right: 0, bottom: 0, left: 0 });
        hasInitialZoomedRef.current = true;
      }

      setSubmarketPolygons(_ => submarketPolygons);
    },
    300
  );

  useEffect(() => {
    if (dashboardLayoutView === 'submarkets' && ['heatmap', 'clusters'].includes(mapMode)) {
      renderHeatmap(null);
    } else {
      resetAllPolygons();
    }
  }, [dashboardLayoutView, mapCopy, mapsCopy]);

  useEffect(() => {
    if (isTabChanging) return;

    // Store previous zoom to detect zoom out
    if (zoom !== null && lastZoomLevel !== null) {
      // If zooming out and we're at a level where clusters should show again
      if (zoom < lastZoomLevel && zoom < CLUSTER_BREAK_ZOOM_LEVEL) {
        setHasClickedCluster(false); // Reset the clicked cluster state on zoom out
      }
    }
    setLastZoomLevel(zoom);

    // Determine whether to show clusters or listings
    if (
      hasClickedCluster ||
      visibleMapClusters.length == 0 ||
      zoom >= CLUSTER_BREAK_ZOOM_LEVEL ||
      (mapMode == 'clusters' && dashboardLayoutView == 'submarkets')
    ) {
      setShouldShowClusters(false);
    } else {
      setShouldShowClusters(true);
    }
  }, [
    zoom,
    visibleMapClusters,
    mapMode,
    heatmapMode,
    isTabChanging,
    hasClickedCluster,
    lastZoomLevel,
  ]);

  useEffect(() => {
    if (['heatmap', 'clusters'].includes(mapMode) && dashboardLayoutView === 'submarkets') {
      // Reset zoom ref when heatmapMode changes to allow initial zoom for new mode
      if (prevHeatmapModeRef.current !== null && prevHeatmapModeRef.current !== heatmapMode) {
        hasInitialZoomedRef.current = false;
      }
      prevHeatmapModeRef.current = heatmapMode;
      renderHeatmap(hoveredSubmarketId);
    } else {
      resetAllPolygons();
    }
  }, [
    submarkets,
    mapMode,
    clusters,
    submarketSortKey,
    mapCopy,
    mapsCopy,
    shouldShowClusters,
    currency,
    heatmapMode,
    matches,
    hoveredSubmarketId,
    hiddenSubmarkets,
    isNewSubmarketDrawingMode,
  ]);

  // POI hotspot overlays: premium-colored radius circles (pins rendered as React children)
  useEffect(() => {
    const clearPoiOverlays = () => {
      poiOverlaysRef.current.forEach(overlay => overlay.setMap(null));
      poiOverlaysRef.current = [];
    };

    clearPoiOverlays();

    if (
      dashboardLayoutView !== 'submarkets' ||
      heatmapMode !== 'hotspot' ||
      mapMode !== 'heatmap' ||
      !mapCopy ||
      !mapsCopy ||
      !sortedPois.length
    ) {
      hasHotspotZoomedRef.current = false;
      return;
    }

    const bounds = new mapsCopy.LatLngBounds();

    sortedPois.forEach((poi, index) => {
      const color = getPoiPremiumColor(poi.avg_premium_factor);
      const position = { lat: poi.lat, lng: poi.lng };
      const isHighlighted = hoveredPoiIndex === index + 1;

      const circle = new mapsCopy.Circle({
        map: mapCopy,
        center: position,
        radius: poi.radius_m || 500,
        fillColor: color,
        fillOpacity: isHighlighted ? 0.5 : 0.25,
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: isHighlighted ? 2.5 : 1.5,
        zIndex: isHighlighted ? 100 : 5 + index * 0.01,
      });

      mapsCopy.event.addListener(circle, 'mouseover', () => {
        setHoveredPoiIndex(index + 1);
      });
      mapsCopy.event.addListener(circle, 'mouseout', () => {
        setHoveredPoiIndex(null);
      });

      poiOverlaysRef.current.push(circle);
      bounds.union(circle.getBounds());
    });

    if (!hasHotspotZoomedRef.current) {
      mapCopy.fitBounds(bounds, { top: 0, right: 0, bottom: 0, left: 0 });
      hasHotspotZoomedRef.current = true;
    }

    return clearPoiOverlays;
  }, [dashboardLayoutView, heatmapMode, mapMode, sortedPois, mapCopy, mapsCopy, hoveredPoiIndex]);

  const poiMapPins = useMemo(() => {
      if (dashboardLayoutView !== 'submarkets' || heatmapMode !== 'hotspot') return false;
      const visibleLabels = getVisiblePoiLabels(sortedPois);
      return sortedPois.map((poi, index) =>
        visibleLabels.has(index) ? (
          <PoiMapPin
            key={`poi-pin-${index}`}
            lat={poi.lat!}
            lng={poi.lng!}
            pin={index + 1}
            name={poi.name}
            premium={typeof poi.avg_premium_factor === 'number' ? formatPremium(poi.avg_premium_factor) : null}
            isHighlighted={hoveredPoiIndex === index + 1}
            onMouseEnter={() => setHoveredPoiIndex(index + 1)}
            onMouseLeave={() => setHoveredPoiIndex(null)}
          />
        ) : null
      );
    },
    [dashboardLayoutView, heatmapMode, sortedPois, hoveredPoiIndex]
  );

  useEffect(() => {
    if (mapMode != 'clusters') {
      hoveredClusterPolygon && hoveredClusterPolygon.setMap(null);
      setHoveredClusterPolygon(null);
      return;
    }

    if (hoveredClusterData) {
      hoveredClusterPolygon && hoveredClusterPolygon.setMap(null);

      const hexagonPath = hoveredClusterData.coordinates.map(coord => ({
        lng: coord[0],
        lat: coord[1],
      }));
      // set polygon to be highlighted
      const polygon = setPolygon(
        mapCopy,
        mapsCopy,
        [hexagonPath],
        COLOR_PRIMARY_2,
        COLOR_PRIMARY_2,
        0,
        0,
        0.2
      );
      setHoveredClusterPolygon(polygon);
    } else {
      hoveredClusterPolygon && hoveredClusterPolygon.setMap(null);
      setHoveredClusterPolygon(null);
    }
  }, [mapMode, hoveredClusterData, mapCopy, mapsCopy]);

  useEffect(() => {
    if (clickedClusterId) {
      const cluster = clusters.find(cluster => cluster.cluster_id == clickedClusterId);
      if (!cluster) return;
      setHasClickedCluster(true);
      applySmartBoundsForCoords(mapCopy, mapsCopy, cluster.coordinates);
    }
  }, [clickedClusterId, clusters, mapCopy, mapsCopy]);

  useEffect(() => {
    if (dashboardLayoutView == 'map') {
      setMapMode('listings');
    } else {
      setMapMode('heatmap');
    }
  }, [dashboardLayoutView]);

  useEffect(() => {
    const updateTopRightUIGroupHeight = () => {
      if (topRightUIGroupRef.current) {
        const height = topRightUIGroupRef.current.offsetHeight;
        setTopRightUIGroupHeight(height);
      }
    };

    updateTopRightUIGroupHeight();

    const resizeObserver = new ResizeObserver(updateTopRightUIGroupHeight);
    if (topRightUIGroupRef.current) {
      resizeObserver.observe(topRightUIGroupRef.current);
    }

    window.addEventListener('resize', updateTopRightUIGroupHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateTopRightUIGroupHeight);
    };
  }, [dashboardLayoutView, topRightUIGroupRef, matches]);

  const Submarketmarkets = useMemo(
    () =>
      dashboardLayoutView == 'submarkets' &&
      submarkets &&
      submarkets !== 'loading' &&
      submarkets.map(submarket => (
        <SubmarketMapMarket
          text={submarket.submarket_name}
          lat={submarket.center.lat}
          lng={submarket.center.lng}
          submarketData={submarket}
          handleOnSampleClick={handleOnSampleClick}
          isSubmarketAccessAllowed={isSubmarketAccessAllowed}
          setDashboardLayoutView={setDashboardLayoutView}
          handleHideSubmarket={handleHideSubmarket}
          isMobileScreen={!matches}
          sx={
            submarket.submarket_id == hoveredSubmarketId
              ? {
                  opacity: 1,
                  pointerEvents: 'auto',
                }
              : {
                  opacity: 0,
                  pointerEvents: 'none',
                }
          }
        />
      )),
    [dashboardLayoutView, submarkets, hiddenSubmarkets, hoveredSubmarketId, matches]
  );

  return (
    <>
      {/* Top Left UI Group */}
      <Box
        sx={{
          position: 'absolute',
          top: matches ? `${mobileHeaderHeight + 10}px` : '25px',
          left: matches ? '15px' : '10px',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          pointerEvents: 'none',
          ...sx,
        }}
      >
        {/* Listing Count Info Box */}
        {isMarketTab && (
          <MapListingCountInfoBox
            open={isMarketTab}
            listingsLength={mapViewportListings.count}
            matches={matches}
          />
        )}
      </Box>

      {/* Top Right UI Group */}
      {!matches && (
        <Box
          ref={topRightUIGroupRef}
          sx={{
            position: 'absolute',
            top: matches ? `${mobileHeaderHeight}px` : '25px',
            right: '15px',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            alignItems: 'flex-end',
            ...(dashboardLayoutView === 'submarkets' && {
              position: 'none',
              width: matches ? '100%' : 'min(100%, 600px)',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start !important',
              mb: 2,
            }),
          }}
        >
          {/* Heatmap Mode Dropdown - Moving from its current position */}
          {dashboardLayoutView == 'submarkets' &&
            mapMode == 'heatmap' &&
            (isClusterHeatmapActive || hasPois) && (
              <DropdownSelect2
                selectedKey={heatmapMode}
                options={heatmapModeOptions}
                setKey={setHeatmapMode}
                isDisabled={false}
              />
            )}

          {dashboardLayoutView == 'submarkets' && MapDrawButton}

          {dashboardLayoutView == 'submarkets' && !matches && (
            <Button
              onClick={() => setShowFullSubmarketMap(!showFullSubmarketMap)}
              variant="outlined"
              size="small"
              sx={{
                color: COLOR_PRIMARY_2,
                borderColor: COLOR_PRIMARY_2,
                '&:hover': {
                  backgroundColor: COLOR_PRIMARY_2,
                  color: 'white',
                },
              }}
            >
              {showFullSubmarketMap ? (
                <FormatListBulletedRoundedIcon sx={{ mr: 1 }} />
              ) : (
                <MapRoundedIcon sx={{ mr: 1 }} />
              )}{' '}
              {showFullSubmarketMap ? 'Split View' : 'Full Map'}{' '}
              {showFullSubmarketMap ? (
                <KeyboardArrowLeftRoundedIcon />
              ) : (
                <KeyboardArrowRightRoundedIcon />
              )}
            </Button>
          )}
        </Box>
      )}

      {/* Bottom Right UI Group */}
      {!matches && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '15px',
            right: '15px',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            alignItems: 'flex-end',
            pointerEvents: 'none',
          }}
        >
          <MapFilter
            search={search}
            toggleDrawer={toggleDrawer}
            setFilterVal={setFilterVal}
            filterVal={filterVal}
            filterActive={filterActive}
            setFilterActive={setFilterActive}
            isDisabled={isDisabled}
            handleOnSampleClick={handleOnSampleClick}
            setZoom={setZoom}
            onlyShowZoom={dashboardLayoutView !== 'map' || matches}
            onZoomInClick={() => {
              if (
                dashboardLayoutView !== 'map' &&
                dashboardLayoutView !== 'compare' &&
                dashboardLayoutView !== 'submarkets'
              ) {
                setDashboardLayoutView('map');
                logAmplitudeEvent('map_viewed_in_overview', { cta: 'zoom_btn' });
              }
            }}
            dashboardLayoutView={dashboardLayoutView}
            noDataInMarket={noDataInMarket}
            bounds={bounds}
            callDynamicMarkers={callDynamicMarkers}
            marketID={marketID}
            showFilterComingSoonAlert={showFilterComingSoonAlert}
            setShowFilterComingSoonAlert={setShowFilterComingSoonAlert}
            filterCacheLoading={filterCacheLoading}
            listingSortingKey={listingSortingKey}
          />

          {dashboardLayoutView == 'map' && (
            <AppliedFiltersDisplay
              filterVal={filterVal}
              setFilterVal={setFilterVal}
              toggleDrawer={toggleDrawer}
              filterActive={filterActive}
              currency={currency}
            />
          )}
        </Box>
      )}
      {/* Main Google Map */}
      <GoogleMapReact
        bootstrapURLKeys={{
          key: GOOGLE_MAPS_API_KEY,
          libraries: ['drawing', 'places', 'visualization'],
          id: 'dashboard2',
          v: '3.64',
        } as any}
        center={mapCenter}
        options={map => mapOptions(map, false)}
        zoom={zoom}
        resetBoundsOnResize={true}
        onChange={handleMapChange}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) =>
          apiIsLoaded(
            map,
            maps,
            search.data.polygon,
            search.data.multiPolygon,
            search.data.boundary
          )
        }
        onChildMouseEnter={(key, childProps) => {
          setHoveredClusterId(key as string);
        }}
        onChildMouseLeave={(key, childProps) => {
          setHoveredClusterId(null);
        }}
        onChildClick={(key, childProps) => {
          setClickedClusterId(key as string);
        }}
        // -150px is the height of the mobile bottom sidebar
        {...(matches &&
          dashboardLayoutView !== 'submarkets' && {
            style: {
              height: collapsed
                ? `calc(100vh - ${mobileHeaderHeight}px - 150px)`
                : `calc(100vh - ${mobileHeaderHeight}px - ${drawerHeight}vh)`,
            },
          })}
        {...(matches &&
          dashboardLayoutView === 'submarkets' && {
            // the 70px deduction si to account for height consumed by submarket filter and controls
            style: {
              height: `calc(100vh - ${mobileHeaderHeight}px - 150px - ${topRightUIGroupHeight}px - 30px)`,
              position: 'relative',
              top: '0',
              left: '0',
              width: '100%',
            },
          })}
      >
        {/* cluster markers */}
        {!isTabChanging &&
          isMarketTab &&
          dashboardLayoutView === 'submarkets' &&
          (mapMode == 'heatmap' || mapMode == 'clusters') &&
          clusterMarkers}

        {/* markers */}
        {!isTabChanging &&
          isMarketTab &&
          visibleMapListings &&
          dashboardLayoutView === 'map' &&
          markers}

        {/* booking markers */}
        {!isTabChanging &&
          !filterApplied &&
          isMarketTab &&
          visibleBookingListings &&
          dashboardLayoutView === 'map' &&
          bookingMarkers}

        {Submarketmarkets}

        {/* POI hotspot pins */}
        {poiMapPins}

        {/* heatmap tooltip */}
        {hoveredClusterData &&
          dashboardLayoutView === 'submarkets' &&
          ((mapMode == 'heatmap' && heatmapMode == 'smartZones') || mapMode == 'clusters') && (
            <ClusterPointer
              marketID={search.data.id}
              lat={hoveredClusterData.centroid.latitude}
              lng={hoveredClusterData.centroid.longitude}
              supply={hoveredClusterData.supply}
              performanceMetrics={{
                adr: hoveredClusterData.adr,
                occupancyRate:
                  hoveredClusterData?.occupancy_rate || hoveredClusterData?.occupancyRate,
                revPar: hoveredClusterData.revPar || 0,
                supply: hoveredClusterData.supply,
                revenue: hoveredClusterData.revenue,
              }}
              clusterKey={clusterKey}
              currency={currency}
              color={COLOR_PRIMARY_2}
              shouldFade={false}
              savedClusters={savedClusters}
              setSavedClusters={setSavedClusters}
            />
          )}

        {/* clicked cluster save button */}
        {rightClickedCluster &&
          dashboardLayoutView === 'submarkets' &&
          mapMode == 'heatmap' &&
          heatmapMode == 'smartZones' && (
            <SaveBoxPointer
              setClickedClusterData={setRightClickedCluster}
              lat={rightClickedCluster.centroid.latitude}
              lng={rightClickedCluster.centroid.longitude}
              currentClusterName={rightClickedCluster?.name}
              savedClusters={savedClusters}
              clusterId={rightClickedCluster?.cluster_id}
              performanceMetrics={{
                adr: rightClickedCluster?.adr,
                occupancyRate: rightClickedCluster?.occupancyRate,
                revPar: rightClickedCluster?.revPar,
                supply: rightClickedCluster?.supply,
                revenue: rightClickedCluster?.revenue,
              }}
              supply={rightClickedCluster?.supply}
              marketID={search.data.id}
              setSavedClusters={setSavedClusters}
              clusterKey={clusterKey}
            />
          )}

        {/* submarket markers */}
        {!isTabChanging &&
          shouldShowSubmarketMarkers &&
          submarketMarkers &&
          submarkets !== 'loading' &&
          !isNewSubmarketDrawingMode &&
          submarketMarkers}

        {/* market in map viewport markers */}
        {!isTabChanging && dashboardLayoutView === 'map' && marketInMapViewportMarkers}
      </GoogleMapReact>

      {mapLoading && (
        <div className="loadingContainer">
          <div className="dotsContainer">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      )}

      {hideConfirmOpen && (
        <DecisionDialog
          open={hideConfirmOpen}
          handleClose={() => setHideConfirmOpen(false)}
          title="Hide Submarket"
          descriptions={['Are you sure you want to hide this submarket?']}
          agreeButtonText="Hide"
          disagreeButtonText="Cancel"
          onAgree={() => {
            handleHideSubmarket(selectedSubmarket.submarketId);
            setHideConfirmOpen(false);
          }}
          onDisagree={() => {
            setSelectedSubmarket(null);
            setHideConfirmOpen(false);
          }}
        />
      )}

      {dashboardLayoutView == 'submarkets' && heatmapMode !== 'hotspot' && (
        <HeatmapLegendBox
          metricsKey={submarketSortKey}
          hideData={false}
          neighbourhoodAnalysisKey={neighbourhoodAnalysisKey}
          neighbourhoodChoroplethLegends={neighbourhoodChoroplethLegends}
          loading={false}
          additionalStyle={{
            top: '80px',
          }}
          legendMap={clusterMetricOptions}
          isLocked={!isSubmarketAccessAllowed}
          handleOnSampleClick={handleOnSampleClick}
          showSubmarketMobileLegend={showSubmarketMobileLegend}
          setShowSubmarketMobileLegend={setShowSubmarketMobileLegend}
        />
      )}

      {dashboardLayoutView == 'submarkets' && heatmapMode === 'hotspot' && hasPois && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '15px',
            left: '15px',
            zIndex: 3,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            px: 1.5,
            pb: 1,
            pt: 0,
            maxWidth: 'calc(100% - 30px)',
          }}
        >
          <HotspotLegend
            isSubmarketAccessAllowed={isSubmarketAccessAllowed}
            handleOnSampleClick={handleOnSampleClick}
          />
        </Box>
      )}

      {/* Replace the existing drawer with our CollapsibleDrawer */}
      {matches && isMarketTab && dashboardLayoutView === 'map' && (
        <CollapsibleDrawer
          drawerHeight={drawerHeight}
          setDrawerHeight={setDrawerHeight}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          listingsCount={activeListingCountDataToUse}
          onClose={() => setDashboardLayoutView('dashboard')}
          setMapHeightOnMobileView={setMapHeightOnMobileView}
          overlayButton={null}
          minDrawerHeight={MIN_DRAWER_HEIGHT}
          maxDrawerHeight={MAX_DRAWER_HEIGHT}
          isOverLayVisible={isDynamicDataBtnVisible}
          components={{
            overview: [
              <Grid container key="overview-heading">
                <Grid item xs={10}>
                  {getSubSectionHeading(
                    getMapViewHeadings(
                      'Map Area Overview',
                      search.data ? marketDisplayName : '',
                      OverViewMapSectionMarketStatsTooltip
                    ),
                    'dashboard-overview-heading'
                  )}
                </Grid>
                <Grid item xs={2} justifyContent={'flex-end'}>
                  {!isDynamicDataBtnVisible && SaveSubMarketButton}
                </Grid>
              </Grid>,
              MainKpiMapView(isMapCollapsed),
              <Box sx={{ marginBottom: '50px' }} key="overview-spacing" />,
            ],
            listings: [
              ListingCountHeader,
              <ClickAwayListener
                onClickAway={() => {
                  setCurrentHoveredCardFromDataGrid(null);
                }}
              >
                {ListingCardGrid}
              </ClickAwayListener>,
            ],
          }}
        />
      )}

      {submarketDrawnOutOfParent && (
        <Modal open={submarketDrawnOutOfParent} onClose={() => setSubmarketDrawnOutOfParent(false)}>
          <Box
            sx={{
              p: 4,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'clamp(200px, 70%, 400px)',
              height: '150px',
              background: 'white',
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              borderRadius: '20px',
              boxShadow: 2,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                background: 'white',
                width: '100%',
                height: '100%',
              }}
            >
              <CloseIcon
                sx={{ cursor: 'pointer', position: 'absolute', top: -20, right: -20 }}
                onClick={() => setSubmarketDrawnOutOfParent(false)}
              />
              <Typography variant="h6">
                You can only create this submarket inside of{' '}
                <span style={{ marginLeft: 1, fontWeight: 600 }}>
                  {parentMarketDetails
                    ? parentMarketDetails.displayName || parentMarketDetails.district
                    : search.data.name || search.data.district}
                </span>
              </Typography>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default React.memo(Map);
